// src/pages/MPFlow/index.jsx
import React, { useState, useEffect } from 'react';
import MPStepper from './components/MPStepper';
import LGPDBanner from './components/LGPDBanner';
import TopPageHeader from '../../ui/PageHeader';
import FreePageHeader from '../../components/FreePageHeader';
import Phase1Profile from './phases/Phase1Profile';
import Phase2Creation from './phases/Phase2Creation';
import Phase3Refine from './phases/Phase3Refine';
import Phase4Segmentation from './phases/Phase4Segmentation';
import Phase5Wallet from './phases/Phase4Wallet';
import Phase6Dashboard from './phases/Phase5Dashboard';
import './mp.css';
import { RocketLaunchIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const PROG_KEY   = 'mp_flow_progress';
const SPLASH_KEY = 'mp_entered';

export default function MPFlowPage({ freeMode = false }) {
  const [splash, setSplash]     = useState(false);
  const [step, setStep]         = useState(1);
  const [profile, setProfile]   = useState(null);
  const [creation, setCreation] = useState(null);
  const [refinement, setRefinement] = useState(null);
  const [targeting, setTargeting]   = useState(null);
  const [campaignId, setCampaignId] = useState(null);
  const [showSignupWall, setShowSignupWall] = useState(false);


  // Detecta se já entrou antes (sem splash)
  useEffect(() => {
    const entered = localStorage.getItem(SPLASH_KEY);
    if (!entered) setSplash(true);

    // Tenta recuperar progresso salvo
    try {
      const prog = JSON.parse(localStorage.getItem(PROG_KEY) || 'null');
      if (prog?.step && prog.step > 1) {
        setStep(prog.step);
        if (prog.profile)    setProfile(prog.profile);
        if (prog.creation)   setCreation(prog.creation);
        if (prog.refinement) setRefinement(prog.refinement);
        if (prog.targeting)  setTargeting(prog.targeting);
        if (prog.campaignId) setCampaignId(prog.campaignId);
      }
    } catch {}
  }, []);

  // Persiste progresso no localStorage a cada passo
  const saveProgress = (newStep, updates = {}) => {
    try {
      const prog = {
        step:        newStep,
        profile:     updates.profile    ?? profile,
        creation:    updates.creation   ?? creation,
        refinement:  updates.refinement ?? refinement,
        targeting:   updates.targeting  ?? targeting,
        campaignId:  updates.campaignId ?? campaignId,
      };
      localStorage.setItem(PROG_KEY, JSON.stringify(prog));
    } catch {}
  };

  const enter = () => {
    localStorage.setItem(SPLASH_KEY, '1');
    setSplash(false);
  };

  const handlePhase1 = (data) => {
    setProfile(data);
    saveProgress(2, { profile: data });
    setStep(2);
  };

  const handlePhase2 = (data) => {
    setCreation(data);
    // Modo free: bloqueia em fase 2, exibe painel de cadastro
    if (freeMode) {
      setShowSignupWall(true);
      return;
    }
    saveProgress(3, { creation: data });
    setStep(3);
  };

  const handlePhase3 = (data) => {
    setRefinement(data);
    saveProgress(4, { refinement: data });
    setStep(4);
  };

  const handlePhase4 = (data) => {
    setTargeting(data.targeting);
    saveProgress(5, { targeting: data.targeting });
    setStep(5);
  };

  const handlePhase5 = ({ campaignId: cid }) => {
    setCampaignId(cid);
    saveProgress(6, { campaignId: cid });
    setStep(6);
  };

  // Detecta se está logado (verifica token no localStorage)
  const isLoggedIn = !!localStorage.getItem('authToken');

  const Header = freeMode ? FreePageHeader : TopPageHeader;

  // ── Splash ────────────────────────────────────────────────────────────────
  if (splash) {
    return (
      <>
        <Header />
        <LGPDBanner />
        <div className="mp-splash">
          <img src="/robot.png" alt="Eddy" className="mp-splash__logo edy-float" />
          <div className="mp-splash__title">Marketing Pro</div>
          <div className="mp-splash__slogan">Aumentar suas vendas</div>
          <p className="mp-splash__desc">
            Crie anúncios que convertem em 5 etapas — sem precisar de agência.
          </p>
          <button className="mp-splash__enter glow-btn" onClick={enter}>Começar agora →</button>
        </div>
      </>
    );
  }

  // ── Signup Wall (free mode) ─────────────────────────────────────────────
  if (showSignupWall) {
    return (
      <>
        <Header />
        <div className="mp-signup-wall">
        <div className="mp-signup-wall__card">
          <div className="mp-signup-wall__emoji"><RocketLaunchIcon className="icon-2xl" /></div>
          <h2 className="mp-signup-wall__title">Seu anúncio está pronto!</h2>
          <p className="mp-signup-wall__sub">
            Crie sua conta grátis para refinar, turbinar e publicar sua campanha
            — sem limites.
          </p>
          <div className="mp-signup-wall__actions">
            <a href="/signup" className="mp-btn mp-btn--primary">Criar conta grátis</a>
            <a href="/login"  className="mp-btn mp-btn--ghost">Já tenho conta</a>
          </div>
          <p className="mp-signup-wall__note"><CheckCircleIcon className="icon-xs icon-inline" />Gratuito &nbsp;·&nbsp; <CheckCircleIcon className="icon-xs icon-inline" />Sem cartão &nbsp;·&nbsp; <CheckCircleIcon className="icon-xs icon-inline" />30 créditos de boas-vindas</p>
        </div>
      </div>
    </>
    );
  }

  // ── Fluxo principal ──────────────────────────────────────────────────────
  return (
    <>
      <Header />
      <LGPDBanner />
      <div className="mp-flow">
        <MPStepper current={step} onStepClick={(s) => { if (s < step) setStep(s); }} />
        <div className="mp-flow__body">
          {step === 1 && (
            <Phase1Profile onNext={handlePhase1} />
          )}
          {step === 2 && profile && (
            <Phase2Creation profile={profile} onNext={handlePhase2} onBack={() => setStep(1)} />
          )}
          {step === 3 && profile && creation && (
            <Phase3Refine profile={profile} creation={creation} campaignId={campaignId} onNext={handlePhase3} onBack={() => setStep(2)} />
          )}
          {step === 4 && profile && creation && refinement && (
            <Phase4Segmentation
              profile={profile}
              creation={creation}
              refinement={refinement}
              onNext={handlePhase4}
              onBack={() => setStep(3)}
            />
          )}
          {step === 5 && profile && creation && refinement && targeting && (
            <Phase5Wallet
              profile={profile}
              creation={creation}
              refinement={refinement}
              targeting={targeting}
              campaignId={campaignId}
              isLoggedIn={isLoggedIn}
              onNext={handlePhase5}
              onBack={() => setStep(4)}
            />
          )}
          {step === 6 && campaignId && (
            <Phase6Dashboard campaignId={campaignId} onBack={() => setStep(5)} />
          )}
        </div>
      </div>
    </>
  );
}
